import { useMemo } from "react";
import { ArrowRight, ExternalLink } from "lucide-react";
import { container, gutters } from "@/components/ds";
import { formatAmountMg, formatUsd, priceTagParts } from "@/components/ds/priceTagFormat";
import { useListings, useVendors } from "@/hooks/useDatabase";
import { isBuyable } from "@/lib/listingPicks";
import { isBhgLabs } from "@/lib/bhgLabs";
import { storeUrl, STORE_COUPON } from "@/lib/attribution";
import { trackBuyClick } from "@/lib/trackClick";
import { trackAffiliateClick } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { useHomeCompounds } from "@/components/home/homeData";
import { HomeHeading, homeSectionY, Skeleton } from "@/components/home/parts";

/**
 * Homepage featured store: four in-stock BHG Labs products with their prices, the code, and one link to the store's
 * front page.
 *
 * Laid out as the store card in MASTER section 6.1, for a shelf of four: the "Featured store" label above the title,
 * each product's name exactly as the store lists it with its size, price per mg and price, the code and whether the
 * prices include it, the store link, and the relationship sentence the owner wrote (2026-10-20). Since direction B
 * (2026-10-24) each product is a white card like every other card on the page: still no gradient, no saturated fill,
 * no animation. The code sits on the same highlighter mark as in the hero.
 *
 * Rules this follows:
 *  - Store names only. Each cell shows the listing's own title (BHG-3 10mg, BH-Morelin 10mg) and never the
 *    compound name next to it.
 *  - The pick is the store's in-stock listings for the compounds people look up most, one per compound. Nothing is
 *    hand-ranked and nothing here claims a "best" price.
 *  - Links go through storeUrl(), so they carry the same attribution, ?ref=zmgqyxku and coupon as every other
 *    store link on the site. The placements (homepage_store_row, homepage_store_row_all) are unchanged.
 *
 * The listings table can drift from the store's real catalogue. This shelf shows exactly what the compound pages
 * show, so keep the listings current and both stay right.
 */
const CARDS = 4;
const CELL = "home-card flex h-full min-h-[148px] flex-col p-4 sm:p-5";

function ShopTheStore({ className }: { className?: string }) {
  return (
    <a
      href={storeUrl("homepage_store_row_all")}
      rel="sponsored"
      onClick={() => trackAffiliateClick({ vendor: "bhg-labs", placement: "homepage_store_row_all" })}
      className={className}
    >
      Shop the store <ArrowRight aria-hidden className="h-4 w-4" />
    </a>
  );
}

export default function HomeStore() {
  const { data: vendors = [], isPending: vendorsPending } = useVendors();
  const { data: listings = [], isPending: listingsPending } = useListings();
  const { data: compounds = [] } = useHomeCompounds();

  const store = vendors.find((v) => isBhgLabs(v));
  const storeName = store?.name ?? "BHG Labs";

  const products = useMemo(() => {
    if (!store) return [];
    const views = new Map(compounds.map((c) => [c.id, c.view_count ?? 0]));
    const seen = new Set<string>();
    return listings
      .filter((l) => l.vendor_id === store.id && isBuyable(l) && l.price > 0 && !!l.affiliate_url)
      .sort((a, b) => (views.get(b.compound_id) ?? 0) - (views.get(a.compound_id) ?? 0))
      .filter((l) => (seen.has(l.compound_id) ? false : (seen.add(l.compound_id), true)))
      .slice(0, CARDS);
  }, [store, listings, compounds]);

  const loading = products.length === 0 && (vendorsPending || listingsPending);

  return (
    <section aria-labelledby="home-store-title" className={cn(container.default, gutters, homeSectionY)}>
      <HomeHeading
        label="Featured store"
        id="home-store-title"
        title={`From the ${storeName} store`}
        summary={
          <>
            <span className="font-semibold text-foreground">20% off your first order</span> with code <span className="code-mark">{STORE_COUPON}</span>. The
            prices below are before the code.
          </>
        }
        action={
          <ShopTheStore className="type-small -my-2.5 hidden min-h-11 shrink-0 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline sm:inline-flex" />
        }
      />
      {(loading || products.length > 0) && (
        <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" aria-busy={loading || undefined}>
          {loading
            ? Array.from({ length: CARDS }).map((_, i) => (
                <li key={i} aria-hidden className={CELL}>
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="mt-2 h-3 w-1/3" />
                  <span className="mt-auto pt-3">
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="mt-2 h-3 w-2/3" />
                  </span>
                </li>
              ))
            : products.map((l) => {
                const headline = priceTagParts({ price: l.price, totalMg: l.total_mg }).headline;
                const hasMg = l.total_mg > 0;
                // Some titles already carry the pack size ("..., 60 Dry-Fill Units"), and "1 vial" says nothing the price
                // line does not, so this line shows only a pack size the title leaves out.
                const quantity =
                  l.quantity && !/^1 vial$/i.test(l.quantity.trim()) && !l.title.toLowerCase().includes(l.quantity.toLowerCase()) ? l.quantity : null;
                return (
                  <li key={l.id} className="min-w-0">
                    <a
                      href={storeUrl("homepage_store_row", l.affiliate_url as string)}
                      rel="sponsored"
                      onClick={() =>
                        trackBuyClick({
                          compoundId: l.compound_id,
                          vendorId: l.vendor_id,
                          listingId: l.id,
                          compoundName: l.title,
                          vendorName: storeName,
                          source: "homepage-store-row",
                        })
                      }
                      className={cn(
                        CELL,
                        "home-card-link group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      )}
                    >
                      {/* The name in full, as the store lists it (MASTER 6.1): never cut short. */}
                      <span className="type-body font-semibold text-foreground group-hover:underline group-hover:underline-offset-4">{l.title}</span>
                      {quantity && <span className="type-small mt-0.5 text-muted-foreground">{quantity}</span>}
                      {/* Price per mg first, then the size and pack price on a line of their own, so nothing is cut short. */}
                      <span className="mt-auto flex items-baseline justify-between gap-2 pt-3">
                        <span className="type-body font-semibold tabular-nums text-foreground">{headline}</span>
                        <span className="type-small inline-flex shrink-0 items-center gap-1 font-medium text-primary">
                          View <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                        </span>
                      </span>
                      <span className="type-small block tabular-nums text-muted-foreground">
                        {hasMg ? `${formatAmountMg(l.total_mg)}, ${formatUsd(l.price)}` : formatUsd(l.price)}
                      </span>
                    </a>
                  </li>
                );
              })}
        </ul>
      )}
      <ShopTheStore className="mt-4 inline-flex h-12 w-full items-center justify-center gap-1 rounded-xl bg-card text-sm font-semibold text-foreground shadow-sm ring-1 ring-border transition-colors duration-150 hover:bg-secondary sm:hidden" />
      {/* The relationship sentence the owner wrote (2026-10-20), last, as in the store card (MASTER 6.1). */}
      <p className="type-small mt-4 text-muted-foreground">{storeName} is a separate company from BodyHackGuide.</p>
    </section>
  );
}
